import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
    MapPin, Plus, Pencil, Trash2, Loader2, Users, ExternalLink, Locate,
    ClipboardList, AlertTriangle, Map as MapIcon,
} from "lucide-react";

export default function CantieriPage() {
    const [sites, setSites] = useState(null);
    const [editSite, setEditSite] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [geoJob, setGeoJob] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/presenze/worksites");
            setSites(data);
        } catch {
            toast.error("Errore nel caricamento dei cantieri");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get("/presenze/worksites/geocode-missing/status")
            .then(({ data }) => { if (data.running) setGeoJob(data); })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!geoJob?.running) return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get("/presenze/worksites/geocode-missing/status");
                setGeoJob(data);
                load();
                if (!data.running) {
                    clearInterval(t);
                    toast.success(`Geolocalizzati ${data.updated} cantieri su ${data.total}`);
                }
            } catch { clearInterval(t); }
        }, 4000);
        return () => clearInterval(t);
    }, [geoJob?.running, load]);

    const missingCount = useMemo(() => (sites || []).filter((s) => s.lat == null).length, [sites]);

    const startGeocodeMissing = async () => {
        try {
            const { data } = await api.post("/presenze/worksites/geocode-missing");
            if (data.started) {
                toast.success(`Geolocalizzazione di ${data.pending} cantieri avviata in background`);
                setGeoJob({ running: true, done: 0, total: data.pending, updated: 0 });
            } else if (data.running) {
                toast.info("Geolocalizzazione già in corso");
                setGeoJob(data);
            } else {
                toast.info("Nessun cantiere da geolocalizzare");
            }
        } catch { toast.error("Errore nell'avvio della geolocalizzazione"); }
    };

    const toggleActive = async (site) => {
        try {
            const { name, client_name, address, lat, lng, radius_m } = site;
            await api.patch(`/presenze/worksites/${site.id}`, {
                name, client_name, address, lat, lng, radius_m, active: !site.active,
            });
            toast.success(site.active ? "Cantiere disattivato" : "Cantiere attivato");
            load();
        } catch {
            toast.error("Errore nel salvataggio");
        }
    };

    const remove = async (site) => {
        if (!window.confirm(`Eliminare il cantiere "${site.name}"?`)) return;
        try {
            await api.delete(`/presenze/worksites/${site.id}`);
            toast.success("Cantiere eliminato");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nell'eliminazione");
        }
    };

    return (
        <div className="mx-auto max-w-6xl px-6 py-8" data-testid="cantieri-page">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <MapPin className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Cantieri</h1>
                        <p className="text-sm text-slate-500">
                            Luoghi di lavoro con area GPS per la timbratura geolocalizzata
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link to="/presenze/cantieri/mappa">
                        <Button variant="outline" className="gap-2" data-testid="cantieri-mappa-btn">
                            <MapIcon className="h-4 w-4" /> Mappa
                        </Button>
                    </Link>
                    {(missingCount > 0 || geoJob?.running) && (
                        <Button variant="outline" onClick={startGeocodeMissing} disabled={geoJob?.running} className="gap-2" data-testid="cantieri-geocode-missing-btn">
                            {geoJob?.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Locate className="h-4 w-4" />}
                            {geoJob?.running ? `Geolocalizzazione in corso ${geoJob.done}/${geoJob.total}…` : `Geolocalizza mancanti (${missingCount})`}
                        </Button>
                    )}
                    <Button onClick={() => setShowCreate(true)} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="cantieri-add-btn">
                        <Plus className="h-4 w-4" /> Nuovo cantiere
                    </Button>
                </div>
            </div>

            {!sites ? (
                <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
            ) : sites.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="cantieri-empty">
                    Nessun cantiere configurato. Crea il primo cantiere per abilitare le timbrature.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                <th className="px-3 py-2.5">Cantiere</th>
                                <th className="px-3 py-2.5">Indirizzo</th>
                                <th className="px-3 py-2.5">Raggio</th>
                                <th className="px-3 py-2.5">Assegnati</th>
                                <th className="px-3 py-2.5">Attività</th>
                                <th className="px-3 py-2.5">Attivo</th>
                                <th className="px-3 py-2.5 text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sites.map((s) => (
                                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`cantiere-row-${s.id}`}>
                                    <td className="px-3 py-2">
                                        <div className="flex flex-wrap items-center gap-1.5 font-semibold text-slate-700">
                                            {s.name}
                                            {s.needs_review && (
                                                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700" data-testid={`cantiere-review-badge-${s.id}`}>DA VERIFICARE</span>
                                            )}
                                        </div>
                                        {s.client_name && <div className="text-[11px] text-slate-400">{s.client_name}</div>}
                                    </td>
                                    <td className="max-w-xs px-3 py-2">
                                        <div className="truncate text-xs text-slate-600">{s.address || "—"}</div>
                                        {s.lat != null && s.lng != null ? (
                                            <a
                                                href={`https://www.google.com/maps?q=${s.lat},${s.lng}`}
                                                target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-[11px] text-[#376DB3] hover:underline"
                                            >
                                                {s.lat.toFixed(5)}, {s.lng.toFixed(5)} <ExternalLink className="h-3 w-3" />
                                            </a>
                                        ) : (
                                            <span className="text-[11px] font-semibold text-amber-600" data-testid={`cantiere-nocoords-${s.id}`}>Coordinate mancanti — modifica e geolocalizza</span>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 font-mono-numbers text-xs text-slate-600">{s.radius_m} m</td>
                                    <td className="px-3 py-2">
                                        <span
                                            className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600"
                                            title="Conteggio automatico: i dipendenti vengono assegnati dalle attività del cantiere"
                                            data-testid={`cantiere-assigned-count-${s.id}`}
                                        >
                                            <Users className="h-3.5 w-3.5" /> {s.assigned_count} dipendenti
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <Link
                                            to={`/presenze/cantieri/${s.id}`}
                                            className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                                            data-testid={`cantiere-attivita-btn-${s.id}`}
                                        >
                                            <ClipboardList className="h-3.5 w-3.5" /> {s.activities_count ?? 0} attività
                                            {s.coverage_alerts > 0 && (
                                                <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700" data-testid={`cantiere-coverage-alert-${s.id}`}>
                                                    <AlertTriangle className="h-3 w-3" /> {s.coverage_alerts}
                                                </span>
                                            )}
                                        </Link>
                                    </td>
                                    <td className="px-3 py-2">
                                        <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`cantiere-active-${s.id}`} />
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center justify-end gap-0.5">
                                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditSite(s)} title="Modifica" data-testid={`cantiere-edit-${s.id}`}>
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => remove(s)} title="Elimina" data-testid={`cantiere-delete-${s.id}`}>
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <SiteDialog
                open={showCreate || !!editSite}
                site={editSite}
                onClose={() => { setShowCreate(false); setEditSite(null); }}
                onSaved={() => { setShowCreate(false); setEditSite(null); load(); }}
            />
        </div>
    );
}

export function SiteDialog({ open, site, onClose, onSaved }) {
    const [name, setName] = useState("");
    const [clientName, setClientName] = useState("");
    const [address, setAddress] = useState("");
    const [lat, setLat] = useState("");
    const [lng, setLng] = useState("");
    const [radius, setRadius] = useState(50);
    const [geoResults, setGeoResults] = useState(null);
    const [geoLoading, setGeoLoading] = useState(false);
    const [mapsLink, setMapsLink] = useState("");
    const [mapsLoading, setMapsLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(site?.name || "");
            setClientName(site?.client_name || "");
            setAddress(site?.address || "");
            setLat(site?.lat != null ? String(site.lat) : "");
            setLng(site?.lng != null ? String(site.lng) : "");
            setRadius(site?.radius_m || 50);
            setGeoResults(null);
            setMapsLink("");
        }
    }, [open, site]);

    const extractFromMapsLink = async () => {
        setMapsLoading(true);
        try {
            const { data } = await api.post("/presenze/geocode-maps-link", { url: mapsLink });
            setLat(String(data.lat));
            setLng(String(data.lng));
            toast.success("Coordinate estratte dal link di Google Maps");
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nell'estrazione delle coordinate");
        } finally {
            setMapsLoading(false);
        }
    };

    const doGeocode = async () => {
        setGeoLoading(true);
        setGeoResults(null);
        try {
            const { data } = await api.post("/presenze/geocode", { address });
            setGeoResults(data);
            if (data.length === 0) toast.warning("Nessun risultato: prova con un indirizzo più preciso");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nella ricerca coordinate");
        } finally {
            setGeoLoading(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                client_name: clientName.trim() || null,
                address: address.trim() || null,
                lat: parseFloat(lat),
                lng: parseFloat(lng),
                radius_m: parseInt(radius, 10),
                active: site ? site.active : true,
            };
            if (Number.isNaN(payload.lat) || Number.isNaN(payload.lng)) {
                toast.error("Inserisci coordinate valide (usa \u201cCerca coordinate\u201d o inseriscile a mano)");
                setSaving(false);
                return;
            }
            if (site) await api.patch(`/presenze/worksites/${site.id}`, payload);
            else await api.post("/presenze/worksites", payload);
            toast.success(site ? "Cantiere aggiornato" : "Cantiere creato");
            onSaved();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-xl overflow-y-auto" data-testid="cantiere-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-slate-800">{site ? "Modifica cantiere" : "Nuovo cantiere"}</DialogTitle>
                    <DialogDescription>Definisci la posizione GPS e il raggio entro cui la timbratura è valida.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Nome cantiere *</Label>
                            <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. I Turchesi Village" data-testid="cantiere-name-input" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Cliente</Label>
                            <Input className="mt-1" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Es. I Turchesi Club Village" data-testid="cantiere-client-input" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Indirizzo</Label>
                        <div className="mt-1 flex gap-2">
                            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Via, numero civico, comune" data-testid="cantiere-address-input" />
                            <Button type="button" variant="outline" onClick={doGeocode} disabled={geoLoading || !address.trim()} className="shrink-0 gap-1.5" data-testid="cantiere-geocode-btn">
                                {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Locate className="h-4 w-4" />}
                                Cerca coordinate
                            </Button>
                        </div>
                        {geoResults && geoResults.length > 0 && (
                            <div className="mt-2 space-y-1 rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="cantiere-geo-results">
                                {geoResults.map((r) => (
                                    <button
                                        key={`${r.lat},${r.lng}`}
                                        type="button"
                                        onClick={() => { setLat(String(r.lat)); setLng(String(r.lng)); setGeoResults(null); toast.success("Coordinate impostate"); }}
                                        className="block w-full rounded px-2 py-1.5 text-left text-xs text-slate-600 hover:bg-[#376DB3]/10"
                                    >
                                        <MapPin className="mr-1 inline h-3 w-3 text-[#376DB3]" />
                                        {r.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Link Google Maps</Label>
                        <div className="mt-1 flex gap-2">
                            <Input
                                value={mapsLink}
                                onChange={(e) => setMapsLink(e.target.value)}
                                placeholder="https://maps.app.goo.gl/..."
                                data-testid="cantiere-maps-link-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={extractFromMapsLink}
                                disabled={mapsLoading || !mapsLink.trim()}
                                className="shrink-0 gap-1.5"
                                data-testid="cantiere-maps-link-btn"
                            >
                                {mapsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                                Estrai coordinate
                            </Button>
                        </div>
                        <p className="mt-1 text-[11px] text-slate-500">
                            Su Google Maps trova il punto esatto, premi <b>Condividi → Copia link</b> e incollalo qui: latitudine e longitudine si compilano da sole.
                        </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Latitudine *</Label>
                            <Input className="mt-1" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="40.4736" data-testid="cantiere-lat-input" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Longitudine *</Label>
                            <Input className="mt-1" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="17.2403" data-testid="cantiere-lng-input" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Raggio (metri) *</Label>
                            <Input className="mt-1" type="number" min={30} max={5000} value={radius} onChange={(e) => setRadius(e.target.value)} data-testid="cantiere-radius-input" />
                        </div>
                    </div>
                    {lat && lng && !Number.isNaN(parseFloat(lat)) && (
                        <a
                            href={`https://www.google.com/maps?q=${lat},${lng}`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-[#376DB3] hover:underline"
                        >
                            Verifica la posizione su Google Maps <ExternalLink className="h-3 w-3" />
                        </a>
                    )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose}>Annulla</Button>
                    <Button onClick={save} disabled={saving || !name.trim()} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="cantiere-save-btn">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Salva
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
